import { Box, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import TypographyLevel from 'shared/components/Typography';
import React, { ReactElement, useState } from 'react';
import { AppFormikTextFieldNumber } from 'shared/components/AppComponents';
import CheckListClientSettings from './Checklist';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Component for client settings.
 * @param {ClientSettingsProps} props - The component props.
 * @returns {ReactElement} React element representing the client settings.
 */
const ClientSettings: React.FC<ClientSettings> = ({
    clientId,
    handleClick,
    statusPage,
    statusReplace,
    setStatusReplace,
    setFilename,
    slaDays,
    archiveDays,
    checkList,
    InputcheckList,
    ChecklistStepData,
    checkListHistory,
    cm9Days,
    reworkDays,
}): ReactElement => {
    const [, setSLADays] = useState<number>(slaDays);
    const [, setcm9Days] = useState<number>(cm9Days);
    const [, setreworkDays] = useState<number>(reworkDays);
    const [, setArchiveDays] = useState<number>(archiveDays);

    return (
        <Box mt={8} className="client-settings">
            <TypographyLevel variant="two" className="client-settings-title">
                Checklist settings
            </TypographyLevel>

            {/* Archive packet after completed in... */}

            <Grid
                container
                spacing={5}
                className="client-settings-archive-packets"
            >
                <Grid
                    className="client-settings-label-archive-packets"
                    item
                    xs={12}
                    sm={12}
                    md={9}
                    lg={10}
                    xl={10.5}
                    alignContent="center"
                >
                    <TypographyLevel
                        variant="four"
                        className="client-settings-label"
                    >
                        Archive Cross check after completed in...
                    </TypographyLevel>
                </Grid>

                <Grid
                    className="client-settings-archive-packets"
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    lg={2}
                    xl={1.5}
                >
                    <AppFormikTextFieldNumber
                        name="archiveDays"
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                            setArchiveDays(parseInt(ev.target.value, 10))
                        }
                        className="styled-action-client"
                        inputProps={{
                            maxLength: 4,
                            inputProps: {
                                maxLength: 4,
                                'aria-label': 'Purge packet in days',
                            },
                        }}
                    />
                    <Stack spacing={0} direction="row" alignItems="center">
                        <TypographyLevel
                            variant="four"
                            className="client-settings-adornment"
                        >
                            Days
                        </TypographyLevel>
                        <Tooltip
                            placement="right-start"
                            title="This is applied to completed and duplication detected packets."
                        >
                            <IconButton>
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Grid>
            </Grid>

            {/* Timing to SLA */}

            <Grid
                container
                spacing={5}
                className="client-settings-timing-sla-packets"
            >
                <Grid
                    className="client-settings-label-timing-sla-packets"
                    item
                    xs={12}
                    sm={12}
                    md={9}
                    lg={10}
                    xl={10.5}
                    alignContent="center"
                >
                    <TypographyLevel
                        variant="four"
                        className="client-settings-label"
                    >
                        Timing to SLA
                    </TypographyLevel>
                </Grid>

                <Grid
                    className="client-settings-timing-sla-packets"
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    lg={2}
                    xl={1.5}
                >
                    <AppFormikTextFieldNumber
                        name="SLADays"
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                            setSLADays(parseInt(ev.target.value, 10))
                        }
                        className="styled-action-client"
                        inputProps={{
                            inputProps: {
                                'aria-label': 'Timing SLA in days',
                            },
                        }}
                    />
                    <TypographyLevel
                        variant="four"
                        className="client-settings-adornment"
                    >
                        Days
                    </TypographyLevel>
                </Grid>
            </Grid>
            <Grid
                container
                spacing={5}
                className="client-settings-timing-cm9-packets"
            >
                <Grid
                    className="client-settings-label-timing-sla-packets"
                    item
                    xs={12}
                    sm={12}
                    md={9}
                    lg={10}
                    xl={10.5}
                    alignContent="center"
                >
                    <TypographyLevel
                        variant="four"
                        className="client-settings-label"
                    >
                        Timing to CM
                    </TypographyLevel>
                </Grid>

                <Grid
                    className="client-settings-timing-cm9-packets"
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    lg={2}
                    xl={1.5}
                >
                    <AppFormikTextFieldNumber
                        name="cm9Days"
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                            setcm9Days(parseInt(ev.target.value, 10))
                        }
                        className="styled-action-client"
                        inputProps={{
                            inputProps: {
                                'aria-label': 'Timing CM in seconds',
                            },
                        }}
                    />
                    <TypographyLevel
                        variant="four"
                        className="client-settings-adornment"
                    >
                        Seconds
                    </TypographyLevel>
                </Grid>
            </Grid>
            <Grid
                container
                spacing={5}
                className="client-settings-timing-rework-packets"
            >
                <Grid
                    className="client-settings-label-timing-sla-packets"
                    item
                    xs={12}
                    sm={12}
                    md={9}
                    lg={10}
                    xl={10.5}
                    alignContent="center"
                >
                    <TypographyLevel
                        variant="four"
                        className="client-settings-label"
                    >
                        Timing to Rework
                    </TypographyLevel>
                </Grid>

                <Grid
                    className="client-settings-timing-rework-packets"
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    lg={2}
                    xl={1.5}
                >
                    <AppFormikTextFieldNumber
                        name="reworkDays"
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                            setreworkDays(parseInt(ev.target.value, 10))
                        }
                        className="styled-action-client"
                        inputProps={{
                            inputProps: {
                                'aria-label': 'Timing rework in days',
                            },
                        }}
                    />
                    <TypographyLevel
                        variant="four"
                        className="client-settings-adornment"
                    >
                        Days
                    </TypographyLevel>
                </Grid>
            </Grid>
            <CheckListClientSettings
                clientId={clientId}
                ChecklistStepData={ChecklistStepData}
                statusPage={statusPage}
                handleClick={handleClick}
                statusReplace={statusReplace}
                setStatusReplace={setStatusReplace}
                setFilename={setFilename}
                placeholder={checkList?.placeHolder ?? ''}
                checkListHistory={checkListHistory}
                updatedAt={new Date(checkList?.lastUpdatedAt ?? new Date())}
                InputcheckList={InputcheckList}
                isReplaceable={checkList?.enabled ?? false}
            />
        </Box>
    );
};

export default ClientSettings;
